import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar } from "@/components/ui/calendar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Calendar as CalendarIcon, Syringe, Copy } from "lucide-react";
import { addDays, format } from "date-fns";
import { useMemo } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { tr } from "@/utils/i18n";

const CALENDAR_MODIFIER_STYLES = {
  inCycle: { backgroundColor: 'rgba(6, 182, 212, 0.2)', color: '#06b6d4' }
};

export function ScheduleTab({
  cycleData, selectedDate, setSelectedDate, startDate,
  getDaySchedule, isDateInCycle, copyToClipboard
}) {
  const { t, language } = useLanguage();
  const calendarModifiers = useMemo(
    () => ({ inCycle: (date) => isDateInCycle(date) }),
    [isDateInCycle]
  );

  return (
    <div className="space-y-6" data-testid="schedule-content">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-zinc-950/50 border-zinc-800 lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-cyan-400" />
              {t('cycleCalendar')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md"
              modifiers={calendarModifiers}
              modifiersStyles={CALENDAR_MODIFIER_STYLES}
              data-testid="cycle-calendar"
            />
            <div className="mt-4 text-sm text-zinc-400">
              <p>{t('cycleStart')}: <span className="text-white">{format(startDate, 'MMM d, yyyy')}</span></p>
              <p>{tr(language, 'Cycle End', 'Fim do Ciclo', 'Fin del Ciclo')}: <span className="text-white">{format(addDays(startDate, cycleData.total_duration_weeks * 7), 'MMM d, yyyy')}</span></p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-950/50 border-zinc-800 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white">
              {tr(language, 'Schedule for', 'Agenda para', 'Agenda para')} {format(selectedDate, 'EEEE, MMMM d')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              {getDaySchedule(selectedDate).length > 0 ? (
                <div className="space-y-3">
                  {getDaySchedule(selectedDate).map((item, index) => (
                    <div
                      key={`schedule-${item}-${index}`}
                      className="flex items-center gap-4 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800 hover:border-cyan-500/30 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                        <Syringe className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{item}</p>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" onClick={() => copyToClipboard(item)} className="text-zinc-400 hover:text-white">
                              <Copy className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{tr(language, 'Copy to clipboard', 'Copiar', 'Copiar')}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
                    <CalendarIcon className="w-8 h-8 text-zinc-600" />
                  </div>
                  <p className="text-zinc-400">{tr(language, 'No injections scheduled for this day', 'Nenhuma injeção agendada para este dia', 'Sin inyecciones programadas para este día')}</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-zinc-950/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">{tr(language, 'Weekly Protocol Overview', 'Visão Semanal do Protocolo', 'Vista Semanal del Protocolo')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
            {Object.entries(cycleData.weekly_schedule || {}).map(([day, items]) => (
              <div
                key={day}
                className={`p-4 rounded-lg border ${items.length > 0 ? 'bg-zinc-900/50 border-cyan-500/20' : 'bg-zinc-950/30 border-zinc-800'}`}
              >
                <h4 className="font-medium text-cyan-400 mb-2">{day}</h4>
                <div className="space-y-1">
                  {items.length > 0 ? items.map((item, i) => (
                    <p key={`item-${i}-${item}`} className="text-xs text-zinc-400 truncate">{item}</p>
                  )) : (
                    <p className="text-xs text-zinc-600">{tr(language, 'Rest day', 'Dia de descanso', 'Día de descanso')}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
